import { InfoItemHtml } from '@chiroup/components/InfoItemHtml';

import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { hcVerbiage } from '../../common/verbiage';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import FallbackImage from '../common/FallbackImage';
import ResponsivePlayer from './ResponsivePlayer';

const AdviceDetail: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { selectedPatient, language } = usePatientPortal();
  const { adviceId } = useParams();
  const primaryColor = selectedPatient?.clinic?.primaryColor || '#77bd43';

  const advice = useMemo(() => {
    return selectedPatient?.advice?.find((a: any) => a.ID === Number(adviceId));
  }, [adviceId, selectedPatient?.advice]);

  const { prevId, nextId } = useMemo(() => {
    const adviceList = selectedPatient?.advice || [];
    const idx = adviceList.findIndex((a: any) => a.ID === Number(adviceId));

    return {
      prevId: adviceList[idx - 1]?.ID || null,
      nextId: adviceList[idx + 1]?.ID || null,
    };
  }, [adviceId, selectedPatient?.advice]);

  if (!advice) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-gray-400 dark:text-gray-500">Advice not found.</p>
        <Link
          to={`${basePath}/advice`}
          className="mt-4 inline-flex text-sm font-medium"
          style={{ color: primaryColor }}
        >
          Back to advice
        </Link>
      </div>
    );
  }

  const hasNav = !!prevId || !!nextId;

  return (
    <div className="flex flex-col gap-5 max-w-3xl">
      <Link
        to={`${basePath}/advice`}
        className="inline-flex items-center gap-1 text-sm text-gray-400 dark:text-gray-500 font-medium"
      >
        <Icon icon={Icons.chevronLeft} className="h-4 w-4" />
        {hcVerbiage.advice[language]}
      </Link>

      {!!advice.video && (
        <div className="relative -mx-4 sm:-mx-6">
          <div className="overflow-hidden rounded-2xl mx-4 sm:mx-6">
            <ResponsivePlayer url={advice.video} />
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        {!!advice.icon && (
          <FallbackImage
            src={advice.icon}
            alt=""
            imgClassName="w-11 h-11 rounded-xl object-contain flex-shrink-0"
          />
        )}
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          {advice.name}
        </h2>
      </div>

      {!!advice.descr && (
        <section className="portal-card p-4 flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            Details
          </h3>
          <div className="prose prose-sm prose-gray dark:prose-invert max-w-none leading-relaxed text-pretty">
            <InfoItemHtml useDivs className="normal-styling" value={advice.descr} />
          </div>
        </section>
      )}

      {hasNav && (
        <div className="flex items-center justify-between pt-2">
          <div>
            {!!prevId && (
              <Link
                to={`${basePath}/advice/${prevId}`}
                className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 dark:text-gray-400"
              >
                <Icon icon={Icons.chevronLeft} className="h-3.5 w-3.5" /> Previous
              </Link>
            )}
          </div>
          <div>
            {!!nextId && (
              <Link
                to={`${basePath}/advice/${nextId}`}
                className="inline-flex items-center gap-1 text-sm font-semibold"
                style={{ color: primaryColor }}
              >
                Next <Icon icon={Icons.chevronRight} className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdviceDetail;
